import api from "api";
import baseApi from ".";
import createQueryFn from "../createQueryFn";
import { GetSignatureRequest, GetSignatureResponse } from "api/bin/getSignature";
import { GetFileRequest, GetFileResponse } from "api/bin/getFile";
import { MediaThumbnail } from "models/MediaThumbnail";
import { UploadMediaRequest } from "api/bin/uploadMedia";
import { MediaEntity } from "models/MediaEntity";
import { DeleteMediaRequest, DeleteMediaResponse } from "api/bin/deleteMedia";
import { AddPhotoCommentResponse, AddPhotoCommentRequest } from "api/bin/addPhotoComment";
import { DeletePhotoCommentResponse, DeletePhotoCommentRequest } from "api/bin/deletePhotoComment";
import { EditPhotoCommentRequest, EditPhotoCommentResponse } from "api/bin/editPhotoComment";
import { getMediaEntity } from "api/bin";
import { GetMediaEntityRequest, GetMediaEntityResponse } from "api/bin/getMediaEntity";

const mediaApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    deleteMedia: builder.mutation<
      DeleteMediaResponse,
      DeleteMediaRequest & { checklistId: string }
    >({
      queryFn: createQueryFn(api.bin.deleteMedia),
      invalidatesTags: (result, error, { checklistId, mediaId }) => [
        { type: "Checklist", id: checklistId },
        { type: "Media", id: mediaId },
        { type: "Media", id: "LIST" },
      ],
    }),

    createPhotoComment: builder.mutation<
      AddPhotoCommentResponse,
      AddPhotoCommentRequest & { checklistId: string }
    >({
      queryFn: createQueryFn(api.bin.addPhotoComment),
      invalidatesTags: (result, error, { checklistId, mediaId }) => [
        { type: "Checklist", id: checklistId },
        { type: "Media", id: mediaId },
      ],
    }),

    deletePhotoComment: builder.mutation<
      DeletePhotoCommentResponse,
      DeletePhotoCommentRequest & { checklistId: string }
    >({
      queryFn: createQueryFn(api.bin.deletePhotoComment),
      invalidatesTags: (result, error, { checklistId }) => [{ type: "Checklist", id: checklistId }],
    }),

    editPhotoComment: builder.mutation<
      EditPhotoCommentResponse,
      EditPhotoCommentRequest & { checklistId: string }
    >({
      queryFn: createQueryFn(api.bin.editPhotoComment),
      invalidatesTags: (result, error, { mediaId, checklistId }) => [
        { type: "Checklist", id: checklistId },
        { type: "Media", id: mediaId },
      ],
    }),

    getFileById: builder.query<GetFileResponse, GetFileRequest>({
      queryFn: createQueryFn(api.bin.getFile),
      providesTags: (result, error, { fileId }) => [{ type: "Media", id: fileId }],
    }),

    getFilesByIds: builder.query<GetFileResponse[], { fileIds: string[] }>({
      queryFn: async ({ fileIds }) => {
        const fileResponses = await Promise.all(
          fileIds.map((fileId) => api.bin.getFile({ fileId }))
        );
        return { data: fileResponses };
      },
      providesTags: [{ type: "Media", id: "LIST" }],
    }),

    getSignatureById: builder.query<GetSignatureResponse, GetSignatureRequest>({
      queryFn: createQueryFn(api.bin.getSignature),
      providesTags: (result, error, { approvalId }) => [{ type: "Signature", id: approvalId }],
    }),

    getThumbnailById: builder.query<MediaThumbnail, { id: string }>({
      queryFn: async ({ id }) => {
        const blob = await api.bin.getThumbnail(id);
        return {
          data: {
            id,
            imgUrl: window.URL.createObjectURL(blob),
            mediaType: blob.type,
          },
        };
      },
      providesTags: (result, error, { id }) => [{ type: "Thumbnail", id }],
    }),

    getThumbnailsByIds: builder.query<MediaThumbnail[], { thumbnailIds: string[] }>({
      queryFn: async ({ thumbnailIds }) => {
        const thumbnails = await Promise.all(thumbnailIds.map((id) => api.bin.getThumbnail(id)));
        return {
          data: thumbnails.map((blob, index) => ({
            id: thumbnailIds[index],
            imgUrl: window.URL.createObjectURL(blob),
            mediaType: blob.type,
          })),
        };
      },
      providesTags: [{ type: "Thumbnail", id: "LIST" }],
    }),

    uploadMedia: builder.mutation<MediaEntity, UploadMediaRequest>({
      queryFn: async (request) => {
        const verification = await api.bin.uploadMedia(request);

        const { checklistId, file } = request;

        const mediaEntity: MediaEntity = {
          id: verification.id,
          imgUrl: window.URL.createObjectURL(file),
          mediaType: file.type,
          checklist: checklistId,
          compressedImage: undefined,
          isLoading: false,
        };
        return { data: mediaEntity };
      },
      invalidatesTags: (result, error, { checklistId }) => [{ type: "Checklist", id: checklistId }],
    }),

    getMediaEntity: builder.query<GetMediaEntityResponse, GetMediaEntityRequest>({
      queryFn: async ({ fileId, checklistId }) => {
        const mediaEntity = await getMediaEntity(fileId, checklistId);
        return { data: mediaEntity };
      },
      providesTags: (result, error, { fileId }) => [{ type: "Media", id: fileId }],
    }),
  }),
});

export const {
  useCreatePhotoCommentMutation,
  useDeletePhotoCommentMutation,
  useEditPhotoCommentMutation,
  useDeleteMediaMutation,
  useGetFileByIdQuery,
  useGetFilesByIdsQuery,
  useGetSignatureByIdQuery,
  useGetThumbnailByIdQuery,
  useGetThumbnailsByIdsQuery,
  useUploadMediaMutation,
  useGetMediaEntityQuery,
} = mediaApi;

export default mediaApi;
